import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator, TrendingUp, Globe, DollarSign,
  ArrowRight, Info, ChevronDown, Smartphone,
  RefreshCw, Shield, PieChart, ArrowLeft, Loader2, Building2, Network
} from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';
import LegacyToolBanner from '../common/LegacyToolBanner';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';

const FLAG_IMAGES = {
  KE: '/flags/ke.png', IN: '/flags/in.png', BR: '/flags/br.png',
  MX: '/flags/mx.png', TR: '/flags/tr.png', BD: '/flags/bd.png',
  ZA: '/flags/za.png', EG: '/flags/eg.png', ET: '/flags/et.png',
};

const CURRENCY_SYMBOLS = { USD: '$', CHF: 'CHF ', AED: 'AED ', GBP: '£', JPY: '¥' };

const FALLBACK_DATA = {
  treasuryRates: { USD: 0.045, CHF: 0.015, AED: 0.05, GBP: 0.045, JPY: 0.005 },
  countries: {
    Kenya: {
      countryCode: 'KE', currency: 'KES', currencyName: 'Kenyan Shilling',
      exchangeRates: { USD: 129, CHF: 145, AED: 35.1, GBP: 164, JPY: 0.82 },
      reserveRatio: 0.0425, mobileLoanRate: 1.08, nplRate: 0.15,
      marketSize: 45000000000, mainProvider: 'M-Shwari (Safaricom)'
    },
    India: {
      countryCode: 'IN', currency: 'INR', currencyName: 'Indian Rupee',
      exchangeRates: { USD: 83, CHF: 93, AED: 22.6, GBP: 105, JPY: 0.56 },
      reserveRatio: 0.045, mobileLoanRate: 0.24, nplRate: 0.035,
      marketSize: 180000000000, mainProvider: 'Paytm / PhonePe'
    },
    Brazil: {
      countryCode: 'BR', currency: 'BRL', currencyName: 'Brazilian Real',
      exchangeRates: { USD: 5.0, CHF: 5.6, AED: 1.36, GBP: 6.3, JPY: 0.034 },
      reserveRatio: 0.21, mobileLoanRate: 0.48, nplRate: 0.035,
      marketSize: 120000000000, mainProvider: 'Nubank / PicPay'
    },
    Mexico: {
      countryCode: 'MX', currency: 'MXN', currencyName: 'Mexican Peso',
      exchangeRates: { USD: 17.5, CHF: 19.6, AED: 4.76, GBP: 22.1, JPY: 0.118 },
      reserveRatio: 0.10, mobileLoanRate: 0.55, nplRate: 0.025,
      marketSize: 85000000000, mainProvider: 'Mercado Pago / BBVA'
    },
    Turkey: {
      countryCode: 'TR', currency: 'TRY', currencyName: 'Turkish Lira',
      exchangeRates: { USD: 32.5, CHF: 36.4, AED: 8.85, GBP: 41.1, JPY: 0.22 },
      reserveRatio: 0.25, mobileLoanRate: 0.65, nplRate: 0.04,
      marketSize: 60000000000, mainProvider: 'Papara / İşbank'
    },
    Bangladesh: {
      countryCode: 'BD', currency: 'BDT', currencyName: 'Bangladeshi Taka',
      exchangeRates: { USD: 110, CHF: 123, AED: 30.0, GBP: 139, JPY: 0.74 },
      reserveRatio: 0.04, mobileLoanRate: 0.18, nplRate: 0.09,
      marketSize: 20000000000, mainProvider: 'bKash / Nagad'
    },
    'South Africa': {
      countryCode: 'ZA', currency: 'ZAR', currencyName: 'South African Rand',
      exchangeRates: { USD: 16.54, CHF: 18.61, AED: 4.5, GBP: 21.02, JPY: 0.105 },
      reserveRatio: 0.025, mobileLoanRate: 0.48, nplRate: 0.049,
      marketSize: 25000000000, mainProvider: 'Vodacom Financial Services'
    },
    Egypt: {
      countryCode: 'EG', currency: 'EGP', currencyName: 'Egyptian Pound',
      exchangeRates: { USD: 47, CHF: 52.6, AED: 12.8, GBP: 59.4, JPY: 0.32 },
      reserveRatio: 0.18, mobileLoanRate: 0.35, nplRate: 0.035,
      marketSize: 35000000000, mainProvider: 'Fawry / Vodafone Cash'
    },
    Ethiopia: {
      countryCode: 'ET', currency: 'ETB', currencyName: 'Ethiopian Birr',
      exchangeRates: { USD: 57, CHF: 63.8, AED: 15.5, GBP: 72, JPY: 0.38 },
      reserveRatio: 0.10, mobileLoanRate: 0.22, nplRate: 0.05,
      marketSize: 8000000000, mainProvider: 'Telebirr / CBE Birr'
    }
  },
  fees: {
    fx_conversion: { rate: 0.0025 }, forwards_brokerage: { rate: 0.0025 },
    arrangement_fee: { rate: 0.0025 }, redemption_fee: { rate: 0.0025 },
    deposit_mobilisation: { rate: 0.01 }, forwards_premium: { rate: 0.20 },
    remittance: { rate: 25, type: 'fixed' }, depositor_interest: { rate: 0.20 },
    fx_depreciation: { rate: 0.20 }
  }
};

const FlagIcon = ({ countryCode, size = 24 }) => {
  const flagSrc = FLAG_IMAGES[countryCode];
  if (!flagSrc) return <Globe className="text-gray-400" style={{ width: size, height: size }} />;
  return <img src={flagSrc} alt={`${countryCode} flag`} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
};

/**
 * Calculate the total deployment across N partner banks in the fractional reserve cascade.
 *
 * Each bank in the cascade receives the previous bank's capital deployment as a new deposit,
 * reserves the required ratio, and deploys the remainder into T-bill investments.
 *
 * Bank 1 deploys: (1-r) × D
 * Bank 2 deploys: (1-r)² × D
 * Bank N deploys: (1-r)^N × D
 *
 * Total across N banks = D × (1-r) × [1 - (1-r)^N] / r
 * Systemic total (N→∞) = D × (1-r) / r = D × (1/r - 1)
 */
const calcNetworkDeployment = (deposit, reserveRatio, numBanks) => {
  const r = reserveRatio;
  const oneMr = 1 - r;
  // Total deployment across N banks in the cascade
  const networkDeployment = deposit * oneMr * (1 - Math.pow(oneMr, numBanks)) / r;
  // Theoretical systemic total (infinite banks)
  const systemicTotal = deposit * oneMr / r;
  const capturePercent = systemicTotal > 0 ? (networkDeployment / systemicTotal) * 100 : 0;

  // Per-bank breakdown for the cascade
  const cascade = [];
  let remaining = deposit;
  for (let i = 0; i < numBanks && remaining > 100; i++) {
    const reserved = remaining * r;
    const deployed = remaining * oneMr;
    cascade.push({ bank: i + 1, deposited: remaining, reserved, deployed });
    remaining = deployed;
  }

  return { networkDeployment, systemicTotal, capturePercent, cascade, firstBankDeployment: deposit * oneMr };
};

const ReturnsEstimator = () => {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [principal, setPrincipal] = useState(10000);
  const [country, setCountry] = useState('Kenya');
  const [networkBanks, setNetworkBanks] = useState(5);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showCascade, setShowCascade] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/estimator/data`);
        if (!response.ok) throw new Error('Failed to fetch data');
        const result = await response.json();
        if (result.success) { setMarketData(result.data); setLastUpdated(result.data.lastUpdated); }
        else throw new Error(result.error);
      } catch (err) {
        console.error('Error fetching estimator data:', err);
        setError(err.message);
        setMarketData(FALLBACK_DATA);
      } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const data = marketData || FALLBACK_DATA;
  const countryData = data.countries[country];
  const treasuryRate = data.treasuryRates[currency];
  const exchangeRate = countryData?.exchangeRates?.[currency] || 1;
  const fees = data.fees;
  const maxPrincipal = countryData ? Math.floor(countryData.marketSize / (1 / countryData.reserveRatio)) : 10000000;
  const minPrincipal = 1000;

  const calculations = useMemo(() => {
    if (!countryData || !fees) return null;

    const fxConversion = fees.fx_conversion?.rate || 0.0025;
    const forwardsBrokerage = fees.forwards_brokerage?.rate || 0.0025;
    const arrangementFee = fees.arrangement_fee?.rate || 0.0025;
    const redemptionFee = fees.redemption_fee?.rate || 0.0025;
    const depositMobilisation = fees.deposit_mobilisation?.rate || 0.01;
    const forwardsPremium = fees.forwards_premium?.rate || 0.20;
    const remittance = fees.remittance?.rate || 25;
    const depositorInterest = fees.depositor_interest?.rate || 0.20;
    const fxDepreciation = fees.fx_depreciation?.rate || 0.20;

    const fxConversionFee = fxConversion * principal;
    const forwardsBrokerageFee = forwardsBrokerage * principal;
    const arrangementFeeAmt = arrangementFee * principal;
    const redemptionFeeAmt = redemptionFee * principal;
    const depositMobilisationFee = depositMobilisation * principal;
    const forwardsPremiumFee = forwardsPremium * principal;
    const platformFees = fxConversionFee + forwardsBrokerageFee + arrangementFeeAmt + redemptionFeeAmt + depositMobilisationFee;
    const totalFees = platformFees + forwardsPremiumFee + remittance;

    const foreignCurrencyDeposited = principal - totalFees;
    const localCurrencyM1 = exchangeRate * foreignCurrencyDeposited;

    // ─── CORRECTED MODEL ───
    // Network deployment: total capital deployed across all partner banks in the cascade
    const network = calcNetworkDeployment(localCurrencyM1, countryData.reserveRatio, networkBanks);
    const networkDeployment = network.networkDeployment;
    const firstBankDeployment = network.firstBankDeployment;
    const systemicM2 = network.systemicTotal;
    const cascadeCapture = network.capturePercent;
    const cascade = network.cascade;

    // Revenue is earned on the capital actually deployed by network banks
    const grossInvestmentRevenue = (1 + countryData.mobileLoanRate) * networkDeployment;
    const defaultLoss = countryData.nplRate * grossInvestmentRevenue;
    const netInvestmentRevenue = grossInvestmentRevenue - defaultLoss;

    const repatriationRate = exchangeRate * (1 + fxDepreciation);
    const fxCapitalForRepatriation = netInvestmentRevenue / repatriationRate;

    const interestToDepositors = depositorInterest * foreignCurrencyDeposited;
    const totalToRepatriate = foreignCurrencyDeposited + interestToDepositors;
    const grossRevenue = fxCapitalForRepatriation - totalToRepatriate;

    // Fee waterfall model (replaces profit split)
    const grossYield = interestToDepositors + grossRevenue;
    const fxHedgeCost = forwardsPremiumFee;
    const netInvestorReturn = grossYield - platformFees;
    const investorROI = (netInvestorReturn / principal) * 100;

    const treasuryReturn = treasuryRate * principal;
    const excessReturn = netInvestorReturn - treasuryReturn;
    const excessReturnPercent = ((netInvestorReturn - treasuryReturn) / principal) * 100;

    // Systemic multiplier (for impact reporting, not revenue)
    const systemicMultiplier = 1 / countryData.reserveRatio;

    return {
      fxConversionFee, forwardsBrokerageFee, arrangementFeeAmt, redemptionFeeAmt,
      depositMobilisationFee, forwardsPremiumFee, platformFees,
      remittanceFee: remittance, totalFees, foreignCurrencyDeposited, localCurrencyM1,
      firstBankDeployment, networkDeployment, systemicM2, cascadeCapture, cascade,
      systemicMultiplier, networkBanks,
      grossInvestmentRevenue, defaultLoss, netInvestmentRevenue,
      fxDepreciation, repatriationRate, fxCapitalForRepatriation, interestToDepositors,
      totalToRepatriate, grossRevenue, grossYield, fxHedgeCost, platformFees,
      netInvestorReturn, investorROI, treasuryReturn, excessReturn, excessReturnPercent,
      depositorInterest
    };
  }, [principal, currency, country, countryData, exchangeRate, fees, treasuryRate, networkBanks]);

  const formatCurrency = (value, curr = currency) => {
    const symbol = CURRENCY_SYMBOLS[curr] || curr + ' ';
    return `${symbol}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;
  const formatLocalCurrency = (value) => `${countryData?.currency || 'USD'} ${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading market data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <LegacyToolBanner />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/products" className="text-sm text-gray-600 hover:text-gray-900">Platform</Link>
              <Link to="/returns-estimator" className="text-sm text-primary-600 font-medium">Returns Estimator</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-gray-900">Sign In</Link>
              <Link to="/register" className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700">Get Started</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm mb-4">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Returns Estimator</h1>
            <p className="text-lg text-gray-600">
              Model foreign-exchange-protected frontier-market yield, powered by Flowbot's partner bank network.
            </p>
            {lastUpdated && <p className="text-xs text-gray-400 mt-2">Market data last updated: {new Date(lastUpdated).toLocaleString()}</p>}
            {error && <p className="text-xs text-gray-600 mt-2">Using cached data - live data temporarily unavailable</p>}
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Panel */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Investment Parameters</h2>
                  <p className="text-sm text-gray-500">Configure your investment scenario</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Currency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Investment Currency</label>
                  <div className="relative">
                    <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl appearance-none bg-white focus:ring-2 focus:ring-primary-500">
                      {Object.keys(data.treasuryRates).map(curr => <option key={curr} value={curr}>{curr}</option>)}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                  <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                    <Info className="w-3 h-3" /> 1-Year Treasury Rate: {formatPercent(treasuryRate)}
                  </p>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Country</label>
                  <div className="relative">
                    <select value={country} onChange={(e) => setCountry(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl appearance-none bg-white focus:ring-2 focus:ring-primary-500">
                      {Object.entries(data.countries).map(([name, c]) => <option key={name} value={name}>{name} ({c.currency})</option>)}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Country Info */}
                {countryData && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <FlagIcon countryCode={countryData.countryCode} size={32} />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{country} Market Data</h3>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Reserve Ratio:</span>
                            <span className="font-medium">{formatPercent(countryData.reserveRatio)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">T-Bill Investment Rate:</span>
                            <span className="font-medium">{formatPercent(countryData.mobileLoanRate)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">NPL Rate:</span>
                            <span className="font-medium">{formatPercent(countryData.nplRate)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Exchange Rate:</span>
                            <span className="font-medium">1 {currency} = {exchangeRate?.toFixed(2)} {countryData.currency}</span>
                          </div>
                          <div className="col-span-2 flex justify-between">
                            <span className="text-gray-500">Main Provider:</span>
                            <span className="font-medium">{countryData.mainProvider}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Principal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Investment Principal</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">{CURRENCY_SYMBOLS[currency]}</span>
                    <input type="number" value={principal}
                      onChange={(e) => setPrincipal(Math.max(minPrincipal, Math.min(maxPrincipal, Number(e.target.value))))}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <input type="range" value={principal} onChange={(e) => setPrincipal(Number(e.target.value))}
                    min={minPrincipal} max={Math.min(maxPrincipal, 10000000)} step={1000}
                    className="w-full mt-3 accent-primary-600" />
                </div>

                {/* Network Banks Slider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    Flowbot Partner Banks on Network: <span className="text-primary-600 font-bold">{networkBanks}</span>
                  </label>
                  <input type="range" value={networkBanks} onChange={(e) => setNetworkBanks(Number(e.target.value))}
                    min={1} max={51} step={1} className="w-full accent-brand-600" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 bank</span>
                    <span>51 banks (Kenya max)</span>
                  </div>
                </div>

                {/* Network Explanation */}
                {calculations && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <RefreshCw className="w-5 h-5 text-gray-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Fractional Reserve Cascade</h4>
                        <p className="text-sm text-gray-700 mt-1">
                          Each partner bank reserves {formatPercent(countryData.reserveRatio)} and deploys the rest into treasury investments.
                          Capital cycles through the network, depositing into the next bank.
                          With <strong>{networkBanks} partner bank{networkBanks > 1 ? 's' : ''}</strong>, the <span className="text-gray-900">Forwards</span><span className="text-primary-600">flow</span>
                          network captures <strong>{calculations.cascadeCapture.toFixed(1)}%</strong> of the systemic
                          multiplier effect ({calculations.systemicMultiplier.toFixed(1)}x).
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Results Panel */}
            {calculations && (
              <div className="space-y-6">
                {/* Return Card — Fee Waterfall */}
                <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl shadow-xl p-8 text-white">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">Net Investor Return</h2>
                      <p className="text-sm text-primary-100">Annual projection ({networkBanks} partner bank{networkBanks > 1 ? 's' : ''})</p>
                    </div>
                    <span className="ml-auto px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full">FOREIGN-EXCHANGE-PROTECTED</span>
                  </div>

                  <div className="space-y-4">
                    {/* Fee Waterfall */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-primary-200">Gross Yield (bank interest)</span>
                        <span className="font-semibold">{formatCurrency(calculations.grossYield)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-primary-200">Foreign Exchange Hedge Cost (forwards premium)</span>
                        <span className="font-semibold">-{formatCurrency(calculations.fxHedgeCost)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-primary-200">Platform Fees (itemized below)</span>
                        <span className="font-semibold">-{formatCurrency(calculations.platformFees)}</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-white/20">
                        <span className="text-white font-bold text-lg">Net Investor Return</span>
                        <span className="text-white font-bold text-lg">{formatCurrency(calculations.netInvestorReturn)}</span>
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-3xl font-bold">{calculations.investorROI.toFixed(2)}% ROI</div>
                    </div>

                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-primary-100">vs {currency} Treasury ({formatPercent(treasuryRate)})</span>
                        <span className="font-semibold text-gray-300">
                          {calculations.excessReturn > 0 ? '+' : ''}{formatCurrency(calculations.excessReturn)}
                        </span>
                      </div>
                      <div className="text-sm text-primary-200">
                        {calculations.excessReturnPercent > 0 ? 'Excess' : 'Under'} return of {Math.abs(calculations.excessReturnPercent).toFixed(2)}%
                      </div>
                    </div>

                    {/* Platform Fee Breakdown */}
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="text-xs text-primary-200 font-semibold uppercase tracking-wider mb-2">Platform Fee Breakdown</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-primary-200">Foreign Exchange Conversion (0.25%)</span><span>{formatCurrency(calculations.fxConversionFee)}</span></div>
                        <div className="flex justify-between"><span className="text-primary-200">Forwards Brokerage (0.25%)</span><span>{formatCurrency(calculations.forwardsBrokerageFee)}</span></div>
                        <div className="flex justify-between"><span className="text-primary-200">Arrangement Fee (0.25%)</span><span>{formatCurrency(calculations.arrangementFeeAmt)}</span></div>
                        <div className="flex justify-between"><span className="text-primary-200">Redemption Fee (0.25%)</span><span>{formatCurrency(calculations.redemptionFeeAmt)}</span></div>
                        <div className="flex justify-between"><span className="text-primary-200">Deposit Mobilisation (1%)</span><span>{formatCurrency(calculations.depositMobilisationFee)}</span></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Network Impact Card */}
                <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-2xl shadow-xl p-6 text-white">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5" /> Systemic Economic Impact
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-200">Bank 1 Deploys</div>
                      <div className="text-lg font-bold">{formatLocalCurrency(calculations.firstBankDeployment)}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-200">Network Deploys ({networkBanks} banks)</div>
                      <div className="text-lg font-bold">{formatLocalCurrency(calculations.networkDeployment)}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-200">Full Systemic M2</div>
                      <div className="text-lg font-bold">{formatLocalCurrency(calculations.systemicM2)}</div>
                    </div>
                  </div>
                  <div className="mt-3 bg-white/10 rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-200">Network cascade capture</span>
                      <span className="font-bold">{calculations.cascadeCapture.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2 mt-2">
                      <div className="bg-white rounded-full h-2 transition-all" style={{ width: `${Math.min(calculations.cascadeCapture, 100)}%` }} />
                    </div>
                    <p className="text-xs text-gray-300 mt-2">
                      Every {CURRENCY_SYMBOLS[currency]}1 invested catalyzes {formatLocalCurrency(Math.round(calculations.systemicM2 / calculations.localCurrencyM1))} of
                      economic activity across the {country} banking system.
                    </p>
                  </div>
                </div>

                {/* Cascade Toggle */}
                <button onClick={() => setShowCascade(!showCascade)}
                  className="w-full flex items-center justify-between bg-white rounded-xl shadow-lg border border-gray-100 p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-900">View Bank-by-Bank Cascade</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showCascade ? 'rotate-180' : ''}`} />
                </button>

                {showCascade && calculations.cascade && (
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Deployment Cascade Across Partner Banks</h3>
                    <div className="space-y-2">
                      {calculations.cascade.map((c, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-700">{c.bank}</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1">
                              <div className="h-6 bg-gray-500 rounded-l text-white text-xs flex items-center px-2"
                                style={{ width: `${(c.deployed / calculations.localCurrencyM1) * 100}%`, minWidth: '70px' }}>
                                {formatLocalCurrency(c.deployed)}
                              </div>
                              <div className="h-6 bg-gray-400 rounded-r text-xs flex items-center px-1"
                                style={{ width: `${(c.reserved / calculations.localCurrencyM1) * 100}%`, minWidth: '30px' }}>
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 w-16 text-right">{((c.deployed / calculations.localCurrencyM1) * 100).toFixed(1)}%</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-500 rounded inline-block"></span> Capital deployed into T-bill investments</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-400 rounded inline-block"></span> Prudential reserves held</span>
                    </div>
                  </div>
                )}

                {/* Full Breakdown */}
                <button onClick={() => setShowBreakdown(!showBreakdown)}
                  className="w-full flex items-center justify-between bg-white rounded-xl shadow-lg border border-gray-100 p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <PieChart className="w-5 h-5 text-gray-400" />
                    <span className="font-medium text-gray-900">View Full Calculation Breakdown</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showBreakdown ? 'rotate-180' : ''}`} />
                </button>

                {showBreakdown && (
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 space-y-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-gray-400" />Fee Deductions
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">Foreign Exchange Conversion (0.25%)</span><span>-{formatCurrency(calculations.fxConversionFee)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Forwards Brokerage (0.25%)</span><span>-{formatCurrency(calculations.forwardsBrokerageFee)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Arrangement Fee (0.25%)</span><span>-{formatCurrency(calculations.arrangementFeeAmt)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Redemption Fee (0.25%)</span><span>-{formatCurrency(calculations.redemptionFeeAmt)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Deposit Mobilisation (1%)</span><span>-{formatCurrency(calculations.depositMobilisationFee)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Forwards Premium (20%)</span><span>-{formatCurrency(calculations.forwardsPremiumFee)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Remittance Fee</span><span>-{formatCurrency(calculations.remittanceFee)}</span></div>
                        <div className="flex justify-between pt-2 border-t font-semibold"><span>Net Deposited</span><span className="text-primary-600">{formatCurrency(calculations.foreignCurrencyDeposited)}</span></div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400" />Currency Conversion & Network Deployment
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">Local Currency Deposit</span><span>{formatLocalCurrency(calculations.localCurrencyM1)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Bank 1 Deploys (cycle 1)</span><span>{formatLocalCurrency(calculations.firstBankDeployment)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Partner Banks on Network</span><span>{networkBanks}</span></div>
                        <div className="flex justify-between pt-2 border-t font-semibold"><span>Total Network Capital Deployed</span><span className="text-primary-600">{formatLocalCurrency(calculations.networkDeployment)}</span></div>
                        <div className="flex justify-between text-xs"><span className="text-gray-400">Systemic M2 impact (all banks)</span><span className="text-gray-400">{formatLocalCurrency(calculations.systemicM2)}</span></div>
                        <div className="flex justify-between text-xs"><span className="text-gray-400">Network cascade capture</span><span className="text-gray-400">{calculations.cascadeCapture.toFixed(1)}%</span></div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-gray-400" />Treasury Investment Returns (Network)
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">Gross Investment Revenue ({networkBanks} banks)</span><span>{formatLocalCurrency(calculations.grossInvestmentRevenue)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Default Loss ({formatPercent(countryData.nplRate)})</span><span className="text-gray-600">-{formatLocalCurrency(calculations.defaultLoss)}</span></div>
                        <div className="flex justify-between pt-2 border-t font-semibold"><span>Net Investment Revenue</span><span className="text-primary-600">{formatLocalCurrency(calculations.netInvestmentRevenue)}</span></div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <PieChart className="w-4 h-4 text-gray-400" />Fee Waterfall — Investor Return
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">Gross Yield (bank interest)</span><span>{formatCurrency(calculations.grossYield)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Foreign Exchange Hedge Cost (forwards premium 20%)</span><span>-{formatCurrency(calculations.fxHedgeCost)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Platform Fees (1% origination + 0.25% × 4)</span><span>-{formatCurrency(calculations.platformFees)}</span></div>
                        <div className="flex justify-between pt-2 border-t font-semibold text-lg"><span>Net Investor Return</span><span className="text-primary-600">{formatCurrency(calculations.netInvestorReturn)}</span></div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-xl p-6 text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Access the Wholesale Funding Marketplace</h3>
                  <p className="text-sm text-gray-600 mb-4">Get immediate foreign-exchange-protected exposure to frontier-market yield through programmatic risk infrastructure.</p>
                  <Link to="/products?tab=impact-investors" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">
                    Get Started<ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-gray-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Important Disclaimer</h3>
                <p className="text-sm text-gray-700">
                  This calculator provides estimates for illustrative purposes only and does not constitute investment advice.
                  Forwardsflow does not hold notes, extend credit, or guarantee investment performance, liquidity, or execution.
                  All investments carry risk including possible loss of principal. Returns are based on the fractional reserve
                  deployment cascade across <span className="text-gray-900">Forwards</span><span className="text-primary-600">flow</span>'s partner bank network.
                  Actual returns depend on the number of active partner banks, market conditions, and investment performance.
                  Please consult a qualified financial advisor.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReturnsEstimator;
